<template>
  <v-container fluid class="place-floor-plans">
    <page-title-area>
      <h1>{{ place ? place.name : '' }}</h1>
    </page-title-area>

    <v-progress-linear v-if="loading" indeterminate />

    <template v-else>
      <!-- ポーリング -->
      <polling-channel
        v-if="pollingForNewFloorPlan"
        :input="pollingState"
        :active="pollingForNewFloorPlan"
        :updateFunction="checkForNewFloorPlan"
        :interval="POLLING_INTERVAL_MS"
        @valueUpdated="onFloorPlanPollingUpdate"
      />

      <!-- ツールバー -->
      <floor-plan-toolbar
        class="floor-plan-toolbar"
        :current-floor-plan="currentFloorPlan"
        :all-floor-plans="allFloorPlans"
        :selected-month="selectedMonth"
        :display-mode="displayMode"
        :detection-filter="detectionFilter"
        :loading-events="loadingEvents"
        :has-unsaved-changes="hasUnsavedChanges"
        :saving="saving"
        :deleting="deleting"
        :exporting="exporting"
        @change="handleFloorPlanSwitch"
        @save-name="handleSaveName"
        @save="saveChanges"
        @cancel="cancelChanges"
        @add="triggerFileInput"
        @delete="handleDelete"
        @download="handleDownload"
        @month-change="handleMonthChange"
        @display-mode-change="displayMode = $event"
        @detection-filter-change="detectionFilter = $event"
      />

      <!-- アップロード中の表示 -->
      <v-alert v-if="isUploadingFloorPlan" type="info" dense class="mb-4">
        {{ $t('msg.uploading') }}
      </v-alert>

      <!-- 配置モードバックドロップ -->
      <div v-if="capturePlacementMode" class="placement-backdrop" />

      <!-- メインレイアウト: キャンバス + サイドバー -->
      <v-row>
        <v-col cols="12" md="8" :class="{ 'placement-mode-canvas': capturePlacementMode }">
          <floor-plan-canvas
            v-if="currentFloorPlan"
            ref="floorPlanCanvas"
            :floor-plan="currentFloorPlan"
            :items="items"
            :sensors="sensors"
            :capture-items="captureItemsForCanvas"
            :capture-placement-mode="capturePlacementMode"
            :display-mode="displayMode"
            :place-name="place ? place.name : ''"
            :selected-month="selectedMonth"
            :detection-filter="detectionFilter"
            :sensor-events="sensorEvents"
            :is-exporting="exporting"
            @change="handleFloorPlanChange"
            @capture-change="handleCaptureRecordChange"
            @capture-place="handleCapturePlaceClick"
            @capture-click="handleCaptureRecordPinClick"
          />
          <empty-floor-plan-state v-if="!currentFloorPlan" @upload="triggerFileInput" />
        </v-col>
        <v-col cols="12" md="4">
          <side-panel
            :items="items"
            :capture-records="captureRecords"
            :current-floor-plan-id="currentFloorPlan ? currentFloorPlan.id : null"
            :all-floor-plans="allFloorPlans"
            :sensors="sensors"
            :floor-plan-objects="currentFloorPlan ? currentFloorPlan.objects || {} : {}"
            :place-name="place ? place.name : ''"
            :floor-plan-name="currentFloorPlan ? currentFloorPlan.name : ''"
            :selected-month="selectedMonth"
            :detection-filter="detectionFilter"
            @remove="handleRemoveItem"
            @capture-edit="handleCaptureRecordEdit"
            @capture-delete="handleCaptureRecordDelete"
            @capture-unplace="handleCaptureRecordUnplace"
            @capture-add-from-map="handleAddFromMap"
            @capture-add-without-position="handleAddWithoutPosition"
          />
        </v-col>
      </v-row>

      <!-- ファイル入力（非表示） -->
      <input
        ref="fileInput"
        type="file"
        accept="image/jpeg,image/png"
        class="d-none"
        data-testid="floor-plan-file-input"
        @change="handleFileSelected"
      />

      <!-- 捕獲記録エディタ -->
      <capture-record-editor
        v-if="featureIsActive('r_type_capture_record')"
        v-model="showCaptureRecordEditor"
        :record="editingCaptureRecord"
        :sensors="sensors"
        :place-id="placeId"
        :default-sensor-ids="defaultSensorIds"
        @saved="handleCaptureRecordSaved"
      />
    </template>
  </v-container>
</template>

<i18n lang="yaml">
ja:
  msg:
    uploading: アップロード中...
    load_failed: データの読み込みに失敗しました
    save_success: 保存しました
    save_failed: 保存に失敗しました
    duplicate_name: 同じ名前の図面が既に存在します
    duplicate_placement: 他の図面に配置済みのセンサーが含まれています
    delete_success: 図面を削除しました
    delete_failed: 削除に失敗しました
    upload_success: 図面をアップロードしました
    upload_failed: アップロードに失敗しました
    upload_timeout: アップロードがタイムアウトしました
    save_name_success: 図面名を変更しました
    save_name_failed: 図面名の変更に失敗しました
    download_failed: 画像のダウンロードに失敗しました
    detection_fetch_failed: 一部のセンサーの検出結果を取得できませんでした
    capture_delete_success: 捕獲記録を削除しました
    capture_delete_failed: 捕獲記録の削除に失敗しました
    capture_record_added: '{year}年{month}月{day}日の捕獲記録を追加しました'
    capture_record_updated: '{year}年{month}月{day}日の捕獲記録を更新しました'
  confirm:
    discard_changes: 未保存の変更があります。破棄しますか？
    delete_floor_plan: この図面を削除しますか？
  validation:
    image_size_too_large: 画像サイズが大きすぎます
    invalid_file_type: JPEGまたはPNG形式の画像を選択してください
en:
  msg:
    uploading: Uploading...
    load_failed: Failed to load data
    save_success: Saved successfully
    save_failed: Failed to save
    duplicate_name: A floor plan with this name already exists
    duplicate_placement: Some sensors are already placed on other floor plans
    delete_success: Floor plan deleted
    delete_failed: Failed to delete
    upload_success: Floor plan uploaded
    upload_failed: Failed to upload
    upload_timeout: Upload timed out
    save_name_success: Floor plan name updated
    save_name_failed: Failed to update floor plan name
    download_failed: Failed to download image
    detection_fetch_failed: Failed to fetch detections for some sensors
    capture_delete_success: Capture record deleted
    capture_delete_failed: Failed to delete capture record
    capture_record_added: 'Capture record added for {year}/{month}/{day}'
    capture_record_updated: 'Capture record for {year}/{month}/{day} updated'
  confirm:
    discard_changes: You have unsaved changes. Discard them?
    delete_floor_plan: Delete this floor plan?
  validation:
    image_size_too_large: Image size is too large
    invalid_file_type: Please select a JPEG or PNG image
</i18n>

<script>
import {
  addMonths, endOfMonth, format, getUnixTime, parse, startOfMonth,
} from 'date-fns';
import AbortControl from '@/libs/AbortControl';
import { createPromiseQueue } from '@/libs/promiseQueue';
import { fetchAllPages } from '@/libs/fetchAllPages';
import { validateImageFile } from '@/libs/imageValidator';
import Features from '@/mixins/features';
import Notifications from '@/mixins/notifications';
import { SENSOR_TYPES } from '@/mixins/sensorTypes';
import PageTitleArea from '@/components/atoms/PageTitleArea';
import PollingChannel from '@/components/atoms/PollingChannel';

import { DETECTION_FILTERS } from './detectionFilters';
import { DISPLAY_MODES } from './displayModes';
import { CAPTURE_OBJECT_TYPE, SENSOR_OBJECT_TYPE, AUTO_LINK_DISTANCE_THRESHOLD } from './floorPlanObjectTypes';
import {
  isCaptureRecordInViewMonth,
  formatCaptureRecordDateParams,
} from './captureRecordDate';
import { markerColorFor } from './markerColors';
import CaptureRecordEditor from '../RTypeDetectionTrend/CaptureRecordEditor';
import EmptyFloorPlanState from './EmptyFloorPlanState';
import FloorPlanCanvas from './FloorPlanCanvas';
import FloorPlanToolbar from './FloorPlanToolbar';
import SidePanel from './SidePanel';

const EVENT_LOAD_CONCURRENCY = 5;
const POLLING_INTERVAL_MS = 2000;
const POLLING_TIMEOUT_MS = 60000;
const MAX_FLOOR_PLAN_IMAGE_SIZE = 10000;

export default {
  name: 'PlaceFloorPlans',
  inject: ['api'],
  mixins: [Features, Notifications],
  components: {
    CaptureRecordEditor,
    EmptyFloorPlanState,
    PageTitleArea,
    PollingChannel,
    FloorPlanCanvas,
    FloorPlanToolbar,
    SidePanel,
  },
  props: {
    placeId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      place: null,
      sensors: [],
      allFloorPlans: [],
      currentFloorPlan: null,
      originalFloorPlan: null,
      sensorEvents: {},
      selectedMonth: format(new Date(), 'yyyy-MM'),
      detectionFilter: DETECTION_FILTERS.EXCLUDE_MISDETECTION,
      displayMode: DISPLAY_MODES.NONE,
      loading: true,
      loadingEvents: false,
      loadEventsGeneration: 0,
      eventLoadAbortControl: new AbortControl(),
      uploading: false,
      deleting: false,
      saving: false,
      exporting: false,
      pollingForNewFloorPlan: false,
      pollingState: null,
      pollingStartTime: null,
      POLLING_INTERVAL_MS,
      captureRecords: [],
      captureRecordAbortControl: new AbortControl(),
      capturePlacementMode: false,
      showCaptureRecordEditor: false,
      editingCaptureRecord: null,
      pendingCaptureCoordinates: null,
      defaultSensorIds: [],
    };
  },
  computed: {
    items() {
      if (!this.place) return [];
      return this.sensors.map(sensor => {
        const objData = this.currentFloorPlan?.objects?.[sensor.id];
        return {
          itemId: sensor.id,
          label: sensor.name,
          coordinates: objData != null ? {
            x: Number(objData.x),
            y: Number(objData.y),
            rotation: Number(objData.rotation || 0),
          } : null,
          isPlaced: objData != null,
          isPlacedOnOtherFloorPlan: this.isPlacedOnOtherFloorPlan(sensor.id),
          placedFloorPlanName: this.getPlacedFloorPlanName(sensor.id),
          count: this.getCountForSensor(sensor.id),
          color: markerColorFor(sensor.type),
          type: sensor.type,
          status: sensor.status,
        };
      });
    },
    hasUnsavedChanges() {
      if (!this.currentFloorPlan || !this.originalFloorPlan) return false;
      return JSON.stringify(this.currentFloorPlan.objects)
        !== JSON.stringify(this.originalFloorPlan.objects)
        || this.currentFloorPlan.name !== this.originalFloorPlan.name;
    },
    isUploadingFloorPlan() {
      return this.uploading || this.pollingForNewFloorPlan;
    },
    captureItemsForCanvas() {
      if (!this.currentFloorPlan) return [];
      return this.captureRecords.map(record => {
        const placement = this.currentFloorPlan.objects
          ? this.currentFloorPlan.objects[record.id]
          : null;
        return {
          id: record.id,
          coordinates: placement != null ? { x: Number(placement.x), y: Number(placement.y) } : null,
          species: record.species,
          count: record.count,
        };
      });
    },
  },
  created() {
    this.eventLoadQueue = createPromiseQueue(EVENT_LOAD_CONCURRENCY);
    this.handleEscapeKey = (event) => {
      if (event.key === 'Escape' && this.capturePlacementMode) {
        this.capturePlacementMode = false;
      }
    };
    document.addEventListener('keydown', this.handleEscapeKey);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleEscapeKey);
    this.captureRecordAbortControl.abort();
    this.eventLoadAbortControl.abort();
  },
  async mounted() {
    try {
      const [place, floorPlans, allSensors] = await Promise.all([
        this.api.fetchRTypePlace(this.placeId),
        this.api.fetchRTypeFloorPlans(this.placeId),
        this.api.fetchSensors(),
      ]);
      this.place = place;
      this.allFloorPlans = floorPlans;
      this.sensors = allSensors.filter(s => place.entity_ids.includes(s.id));
      if (floorPlans.length > 0) {
        this.selectFloorPlan(floorPlans[0]);
      }
      await this.loadCaptureRecords();
      await this.loadEvents();
    } catch (error) {
      this.notifyError('msg.load_failed');
    } finally {
      this.loading = false;
    }
  },
  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      // eslint-disable-next-line no-alert
      if (window.confirm(this.$t('confirm.discard_changes'))) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },
  watch: {
    // ダイアログを閉じたタイミングで配置モードの保留状態をリセットする。
    // 保存完了経路だけでなくキャンセル経路でも確実にクリアし、座標残留を防ぐ。
    showCaptureRecordEditor(visible) {
      if (!visible) {
        this.pendingCaptureCoordinates = null;
        this.editingCaptureRecord = null;
      }
    },
  },
  methods: {
    selectFloorPlan(floorPlan) {
      this.capturePlacementMode = false;
      this.currentFloorPlan = JSON.parse(JSON.stringify(floorPlan));
      this.originalFloorPlan = JSON.parse(JSON.stringify(floorPlan));
    },

    // --- 捕獲記録取得 ---
    async loadCaptureRecords() {
      const signal = this.captureRecordAbortControl.newSignal();
      const monthDate = parse(this.selectedMonth, 'yyyy-MM', new Date());
      const fromDate = format(startOfMonth(monthDate), 'yyyy-MM-dd');
      const toDate = format(endOfMonth(monthDate), 'yyyy-MM-dd');
      try {
        const params = { from: fromDate, to: toDate };
        this.captureRecords = await this.api.fetchRTypeCaptureRecords(this.placeId, params, { signal }) || [];
      } catch (error) {
        if (AbortControl.isAbortError(error)) return;
        throw error;
      }
    },

    // --- イベント取得 ---
    async loadEvents() {
      this.loadEventsGeneration += 1;
      const currentGeneration = this.loadEventsGeneration;
      this.eventLoadQueue.clear();
      // 待機中のタスクはキューから捨てられるが、実行中の取得は止まらない。
      // 中断信号を張り替えて、前の月の取得そのものを打ち切る。
      const signal = this.eventLoadAbortControl.newSignal();
      this.loadingEvents = true;
      try {
        const eventMap = await this.fetchEventsForAllSensors(
          this.selectedMonthRange(),
          this.selectedMonthClosedRange(),
          signal,
        );

        // staleな結果を破棄（月が切り替わった場合）
        if (currentGeneration !== this.loadEventsGeneration) return;
        this.sensorEvents = eventMap;
      } finally {
        if (currentGeneration === this.loadEventsGeneration) {
          this.loadingEvents = false;
        }
      }
    },

    // 表示月の期間を半開区間 [from, to) の unix 秒で返す。イベント取得 API はこの解釈で絞り込む
    selectedMonthRange() {
      const monthDate = parse(this.selectedMonth, 'yyyy-MM', new Date());
      return {
        from: getUnixTime(startOfMonth(monthDate)),
        to: getUnixTime(startOfMonth(addMonths(monthDate, 1))),
      };
    },

    // 表示月の期間を閉区間 [from, to] の unix 秒で返す。
    // RatSense は from / to が指す日を両端とも含むため、月末日を to に置く。
    // 半開区間の to（翌月 1 日）を渡すと翌月 1 日分まで取得してしまう。
    selectedMonthClosedRange() {
      const monthDate = parse(this.selectedMonth, 'yyyy-MM', new Date());
      return {
        from: getUnixTime(startOfMonth(monthDate)),
        to: getUnixTime(endOfMonth(monthDate)),
      };
    },

    async fetchEventsForAllSensors(range, closedRange, signal) {
      const results = await Promise.all(
        this.sensors.map(sensor =>
          this.eventLoadQueue.enqueue(async () => {
            const events = await this.fetchDetections(sensor, range, closedRange, signal);
            return { sensorId: sensor.id, events };
          // 1センサーのエラーで全体の表示を止めない
          }).catch(error => ({
            sensorId: sensor.id,
            events: [],
            // 月の切り替えで打ち切った取得は利用者から見れば失敗ではないため、
            // 意図的に失敗として数えず通知の対象から外す
            failed: !AbortControl.isAbortError(error),
          }))),
      );
      // 取得失敗の通知は 1 度にまとめる。
      if (results.some(result => result.failed)) {
        this.notifyError('msg.detection_fetch_failed');
      }
      const eventMap = {};
      results.forEach(({ sensorId, events }) => {
        eventMap[sensorId] = events;
      });
      return eventMap;
    },

    async fetchDetections(sensor, range, closedRange, signal) {
      if (sensor.type === SENSOR_TYPES.RAT_SENSE) {
        // RatSense は 1 回の応答で全件返るため、ページ送りの仕組みを通さない
        const response = await this.api.fetchRatSenseDetections(sensor.id, closedRange, { signal });
        return response.events || [];
      }
      return this.fetchAllSensorEvents(sensor.id, range, signal);
    },

    async fetchAllSensorEvents(sensorId, params, signal) {
      return fetchAllPages(
        pageParams => this.api.fetchSensorEvents(sensorId, pageParams, { signal }),
        params,
      );
    },

    // --- 他図面配置判定 ---
    isPlacedOnOtherFloorPlan(sensorId) {
      const currentId = this.currentFloorPlan?.id;
      if (!currentId) return false;
      return this.allFloorPlans.some(
        fp => fp.id !== currentId && fp.objects?.[sensorId],
      );
    },

    getPlacedFloorPlanName(sensorId) {
      const currentId = this.currentFloorPlan?.id;
      if (!currentId) return null;
      const fp = this.allFloorPlans.find(
        f => f.id !== currentId && f.objects?.[sensorId],
      );
      return fp?.name || null;
    },

    // --- 検出数計算 ---
    getCountForSensor(sensorId) {
      const events = this.sensorEvents[sensorId] || [];
      if (this.detectionFilter === DETECTION_FILTERS.EXCLUDE_MISDETECTION) {
        return events.filter(e => e.detected && !e.misdetection).length;
      }
      if (this.detectionFilter === DETECTION_FILTERS.PICKUP_ONLY) {
        return events.filter(e => e.pickup).length;
      }
      // ALL: 全検出（detected=true）
      return events.filter(e => e.detected).length;
    },

    // --- 図面操作 ---
    handleFloorPlanChange({ itemId, coordinates }) {
      if (!this.currentFloorPlan.objects) {
        this.$set(this.currentFloorPlan, 'objects', {});
      }
      this.$set(this.currentFloorPlan.objects, itemId, {
        x: String(Math.round(coordinates.x)),
        y: String(Math.round(coordinates.y)),
        rotation: String(Math.round(coordinates.rotation || 0)),
        type: SENSOR_OBJECT_TYPE,
      });
    },

    handleRemoveItem(itemId) {
      this.$delete(this.currentFloorPlan.objects, itemId);
    },

    confirmDiscardChanges() {
      if (!this.hasUnsavedChanges) return true;
      // eslint-disable-next-line no-alert
      return window.confirm(this.$t('confirm.discard_changes'));
    },

    handleFloorPlanSwitch(floorPlanId) {
      if (!this.confirmDiscardChanges()) return;
      const fp = this.allFloorPlans.find(f => f.id === floorPlanId);
      if (fp) this.selectFloorPlan(fp);
    },

    // --- 保存 ---
    buildObjectsFromItems() {
      const objects = {};
      // センサー
      this.items.forEach((item) => {
        if (item.isPlaced) {
          objects[item.itemId] = this.currentFloorPlan.objects[item.itemId];
        }
      });
      // 捕獲記録
      if (this.currentFloorPlan && this.currentFloorPlan.objects) {
        Object.entries(this.currentFloorPlan.objects).forEach(([key, obj]) => {
          if (obj.type === CAPTURE_OBJECT_TYPE) {
            objects[key] = obj;
          }
        });
      }
      return objects;
    },

    async saveChanges() {
      this.saving = true;
      try {
        const objects = this.buildObjectsFromItems();
        const updated = await this.api.updateRTypeFloorPlan(this.currentFloorPlan.id, {
          name: this.currentFloorPlan.name,
          objects,
        });
        this.originalFloorPlan = JSON.parse(JSON.stringify(this.currentFloorPlan));
        // allFloorPlansも更新
        const idx = this.allFloorPlans.findIndex(f => f.id === updated.id);
        if (idx >= 0) this.$set(this.allFloorPlans, idx, updated);
        this.notifySuccess('msg.save_success');
      } catch (error) {
        const errorCode = error.response?.data?.error;
        if (errorCode === 'duplicate_floor_plan_name') {
          this.notifyError('msg.duplicate_name');
        } else if (errorCode === 'duplicate_placement') {
          this.notifyError('msg.duplicate_placement');
        } else {
          this.notifyError('msg.save_failed');
        }
      } finally {
        this.saving = false;
      }
    },

    cancelChanges() {
      this.currentFloorPlan = JSON.parse(JSON.stringify(this.originalFloorPlan));
    },

    // --- 名前変更（即時API保存） ---
    async handleSaveName(newName) {
      try {
        const updated = await this.api.updateRTypeFloorPlan(this.currentFloorPlan.id, {
          name: newName,
          objects: this.originalFloorPlan.objects,
        });
        this.currentFloorPlan.name = updated.name;
        this.originalFloorPlan.name = updated.name;
        const idx = this.allFloorPlans.findIndex(f => f.id === updated.id);
        if (idx >= 0) this.$set(this.allFloorPlans, idx, updated);
        this.notifySuccess('msg.save_name_success');
      } catch (error) {
        if (error.response?.data?.error === 'duplicate_floor_plan_name') {
          this.notifyError('msg.duplicate_name');
        } else {
          this.notifyError('msg.save_name_failed');
        }
      }
    },

    // --- 月変更 ---
    async handleMonthChange(newMonth) {
      this.selectedMonth = newMonth;
      await this.loadEvents();
      try {
        await this.loadCaptureRecords();
      } catch (error) {
        this.notifyError('msg.load_failed');
      }
    },

    // --- 削除 ---
    async handleDelete() {
      // eslint-disable-next-line no-alert
      if (!window.confirm(this.$t('confirm.delete_floor_plan'))) return;
      this.deleting = true;
      try {
        await this.api.deleteRTypeFloorPlan(this.currentFloorPlan.id);
        this.allFloorPlans = this.allFloorPlans.filter(
          f => f.id !== this.currentFloorPlan.id,
        );
        if (this.allFloorPlans.length > 0) {
          this.selectFloorPlan(this.allFloorPlans[0]);
        } else {
          this.currentFloorPlan = null;
          this.originalFloorPlan = null;
        }
        this.notifySuccess('msg.delete_success');
      } catch (error) {
        this.notifyError('msg.delete_failed');
      } finally {
        this.deleting = false;
      }
    },

    // --- アップロード ---
    async handleFileSelected(event) {
      const file = event.target.files[0];
      if (!file) return;
      this.uploading = true;
      try {
        const validation = await validateImageFile(file, {
          allowedTypes: ['jpeg', 'png'],
          maxWidth: MAX_FLOOR_PLAN_IMAGE_SIZE,
          maxHeight: MAX_FLOOR_PLAN_IMAGE_SIZE,
        });
        if (!validation.isValid) {
          this.notifyError(`validation.${validation.reason}`);
          return;
        }
        const response = await this.api.createRTypeFloorPlan(this.placeId, {
          name: file.name.replace(/\.[^/.]+$/, ''),
          contentType: file.type,
        });
        await this.api.uploadToPresignedUrl(response.uploadUrl, file);
        this.pollingState = { id: response.floorPlanId };
        this.pollingStartTime = Date.now();
        this.pollingForNewFloorPlan = true;
      } catch (error) {
        if (error.response?.data?.error === 'duplicate_floor_plan_name') {
          this.notifyError('msg.duplicate_name');
        } else {
          this.notifyError('msg.upload_failed');
        }
      } finally {
        this.uploading = false;
        event.target.value = '';
      }
    },

    // --- ポーリング ---
    async checkForNewFloorPlan() {
      if (Date.now() - this.pollingStartTime > POLLING_TIMEOUT_MS) {
        this.pollingForNewFloorPlan = false;
        this.notifyError('msg.upload_timeout');
        return this.pollingState;
      }
      try {
        const floorPlans = await this.api.fetchRTypeFloorPlans(this.placeId, { isBackgroundRequest: true });
        const newFp = floorPlans.find(fp => fp.id === this.pollingState.id);
        if (newFp && newFp.image_url) {
          this.allFloorPlans = floorPlans;
          return { id: this.pollingState.id, found: true, floorPlan: newFp };
        }
      } catch (error) {
        // 画面の自動更新ポーリングのため握りつぶす（次回インターバルで回復するためユーザー通知不要）
      }
      return this.pollingState;
    },

    onFloorPlanPollingUpdate(updatedState) {
      if (updatedState.found) {
        this.pollingForNewFloorPlan = false;
        this.selectFloorPlan(updatedState.floorPlan);
        this.notifySuccess('msg.upload_success');
      }
    },

    async handleDownload() {
      this.exporting = true;
      // exporting=true による v-if 反映を待ってから toPng を呼ぶ
      // （rotation UI が DOM から消えるのを待つため）
      await this.$nextTick();
      try {
        await this.$refs.floorPlanCanvas.exportImage();
      } catch (error) {
        this.notifyError('msg.download_failed');
      } finally {
        this.exporting = false;
      }
    },

    triggerFileInput() {
      this.$refs.fileInput.click();
    },

    // --- 捕獲記録 CRUD ---

    handleCaptureRecordSaved(savedRecord) {
      // saved emit は Editor の close() より前に発火するため、この時点で
      // editingCaptureRecord は新規作成時のみ null、編集時は対象が入っている
      const isEditing = this.editingCaptureRecord != null;
      const inViewMonth = isCaptureRecordInViewMonth(savedRecord.date, this.selectedMonth);

      this.applyCaptureRecordToList(savedRecord, inViewMonth);
      this.notifySuccess(
        isEditing ? 'msg.capture_record_updated' : 'msg.capture_record_added',
        formatCaptureRecordDateParams(savedRecord.date),
      );
      // 配置モードで追加した場合、objectsに配置情報を追加
      if (this.pendingCaptureCoordinates != null) {
        this.placeCaptureRecord(savedRecord.id, this.pendingCaptureCoordinates);
      }

      // pendingCaptureCoordinates / editingCaptureRecord のクリアは
      // showCaptureRecordEditor watcher 側でクローズ経路をまとめて処理する。
      this.showCaptureRecordEditor = false;
    },

    applyCaptureRecordToList(savedRecord, isInViewMonth) {
      const index = this.captureRecords.findIndex(r => r.id === savedRecord.id);
      if (isInViewMonth) {
        if (index >= 0) {
          this.$set(this.captureRecords, index, savedRecord);
        } else {
          this.captureRecords.push(savedRecord);
        }
      } else if (index >= 0) {
        this.captureRecords.splice(index, 1);
      }
    },

    async handleCaptureRecordDelete(record) {
      try {
        await this.api.deleteRTypeCaptureRecord(record.id);
        this.removeLocalCaptureRecord(record.id);
        this.notifySuccess('msg.capture_delete_success');
      } catch (error) {
        // axiosの 404 = サーバ側で既に削除済み（delete_r_type_capture_record.py が
        // Forbidden/DoesNotExist を404に丸める）。楽観的削除としてローカル状態を整える
        if (error.response?.status === 404) {
          this.removeLocalCaptureRecord(record.id);
        } else {
          this.notifyError('msg.capture_delete_failed');
        }
      }
    },

    removeLocalCaptureRecord(recordId) {
      this.captureRecords = this.captureRecords.filter(r => r.id !== recordId);
      if (this.currentFloorPlan && this.currentFloorPlan.objects) {
        this.$delete(this.currentFloorPlan.objects, recordId);
      }
      if (this.originalFloorPlan && this.originalFloorPlan.objects) {
        this.$delete(this.originalFloorPlan.objects, recordId);
      }
    },

    // 配置解除は currentFloorPlan のみ変更し、originalFloorPlan には反映しない。
    // 「変更を破棄」で配置が復活する＝保存対象として扱う設計（削除とは異なる）。
    handleCaptureRecordUnplace(record) {
      if (!this.currentFloorPlan || !this.currentFloorPlan.objects) return;
      this.$delete(this.currentFloorPlan.objects, record.id);
    },

    handleCaptureRecordPinClick(captureItem) {
      const record = this.captureRecords.find(r => r.id === captureItem.id);
      if (record) {
        this.handleCaptureRecordEdit(record);
      }
    },

    handleCaptureRecordEdit(record) {
      this.editingCaptureRecord = record;
      this.defaultSensorIds = [];
      this.showCaptureRecordEditor = true;
    },

    // --- 捕獲記録 配置 ---

    handleCaptureRecordChange({ id, coordinates }) {
      this.placeCaptureRecord(id, coordinates);
    },

    placeCaptureRecord(recordId, coordinates) {
      if (!this.currentFloorPlan) return;
      if (!this.currentFloorPlan.objects) {
        this.$set(this.currentFloorPlan, 'objects', {});
      }
      this.$set(this.currentFloorPlan.objects, recordId, {
        x: String(Math.round(coordinates.x)),
        y: String(Math.round(coordinates.y)),
        rotation: '0',
        type: CAPTURE_OBJECT_TYPE,
      });
    },

    // --- 配置モード ---

    handleAddFromMap() {
      this.capturePlacementMode = true;
    },

    handleAddWithoutPosition() {
      this.editingCaptureRecord = null;
      this.pendingCaptureCoordinates = null;
      this.defaultSensorIds = [];
      this.showCaptureRecordEditor = true;
    },

    handleCapturePlaceClick({ coordinates }) {
      this.capturePlacementMode = false;
      this.pendingCaptureCoordinates = coordinates;
      this.defaultSensorIds = this.findNearbySensors(coordinates);
      this.editingCaptureRecord = null;
      this.showCaptureRecordEditor = true;
    },

    // --- 自動紐付け ---

    findNearbySensors(coordinates) {
      if (!this.currentFloorPlan || !this.currentFloorPlan.objects) return [];
      const imageDimensions = this.$refs.floorPlanCanvas
        ? this.$refs.floorPlanCanvas.imageDimensions
        : null;
      const minDimension = imageDimensions ? imageDimensions.minDimension : 0;
      const threshold = minDimension > 0
        ? minDimension * AUTO_LINK_DISTANCE_THRESHOLD
        : 150;
      return Object.entries(this.currentFloorPlan.objects)
        .filter(([, obj]) => {
          if (obj.type !== SENSOR_OBJECT_TYPE) return false;
          const dx = Number(obj.x) - coordinates.x;
          const dy = Number(obj.y) - coordinates.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          return distance <= threshold;
        })
        .map(([key]) => key);
    },
  },
};
</script>

<style scoped>
.place-floor-plans {
  position: relative;
}

.floor-plan-toolbar {
  position: sticky;
  top: 0;
  z-index: 20;
}

.placement-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  z-index: 50; /* 配置モードオーバーレイ: global-nav(30-39)より上、modal(100)より下 */
  pointer-events: none;
}

.placement-mode-canvas {
  position: relative;
  z-index: 51; /* 配置モードキャンバス: backdropより上 */
}
</style>
